import { useEffect, useMemo, useRef, useState } from 'react';
import i18n from '../../i18n';
import { useTeamsStore } from '../../store/teamsStore';
import { useNotificationStore } from '../../store/notificationStore';
import { roleForTeam } from '../../extensions/bundled/task-manager/boardRoles';
import { applyAutoClose, planAutoClose } from '../../extensions/bundled/task-manager/commitAutoClose';
import { formatTaskKey, parseTaskKey } from '../../extensions/bundled/task-manager/taskKeys';
import { localPathOf } from '../../services/git/codeRepoClones';
import { loadCodeRepoLog } from '../../services/git/codeRepoCommits';
import { useBoardRepos } from './useTaskCommits';
import { isTauri } from '../../utils/isTauri';

const EMPTY = [];

/**
 * Close the open board's tasks from landed commits (commit ↔ task links,
 * slice 3): reads the board's project repositories on this device, and when a
 * commit on the remote default branch says "Fixes WEB-4", WEB-4 is closed —
 * see commitAutoClose.js for the guards (board switch + since, once per task
 * and commit, never an already closed task).
 *
 * Writers only, like the key fill (a viewer's push is rejected and jams the
 * outbox), desktop only (local git), never in the undo history. Runs when the
 * board opens and whenever the board or the commits change.
 */
export default function useCommitAutoClose({ board, readOnly, updateBoard, taskKeys, keyAliases = EMPTY }) {
  const desktop = isTauri();
  const teams = useTeamsStore((s) => s.teams);
  const enabled = desktop && !!board?.closeFromCommits;
  const { repos } = useBoardRepos(enabled ? board.id : null);
  const [commits, setCommits] = useState(EMPTY);
  const announced = useRef(new Set());
  const teamId = board?.visibility === 'team' && board?.teamId != null ? String(board.teamId) : null;

  const repoSig = (repos || []).map((r) => `${r.id}:${localPathOf(r)}`).join('|');
  useEffect(() => {
    if (!enabled || !repos?.length) {
      setCommits(EMPTY);
      return undefined;
    }
    let alive = true;
    Promise.all(
      repos.map((r) => {
        const path = localPathOf(r);
        if (!path) return [];
        return loadCodeRepoLog(path)
          .then((log) => log.commits.map((c) => ({ ...c, repo: r, repoId: r.id })))
          .catch(() => []);
      })
    ).then((lists) => alive && setCommits(lists.flat()));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- repoSig is the repo set
  }, [enabled, repoSig]);

  const keysOf = useMemo(
    () => (task) => {
      const key = taskKeys?.get(String(task.id));
      const parsed = parseTaskKey(key);
      if (!parsed) return key ? [key] : [];
      return [key, ...keyAliases.map((p) => formatTaskKey(p, parsed.number)).filter(Boolean)];
    },
    [taskKeys, keyAliases]
  );

  useEffect(() => {
    if (!enabled || readOnly || !commits.length || !board) return;
    if (teamId) {
      const role = roleForTeam(teams, teamId);
      if (!role || role === 'viewer') return;
    }
    const plan = planAutoClose(board, commits, keysOf);
    if (!plan.length) return;
    updateBoard((prev) => (prev?.id !== board.id ? prev : applyAutoClose(prev, planAutoClose(prev, commits, keysOf))), { history: false });
    for (const { taskId, key, commit } of plan) {
      const id = `${taskId}:${commit.oid}`;
      if (announced.current.has(id)) continue;
      announced.current.add(id);
      useNotificationStore
        .getState()
        .success(i18n.t('taskLinks.autoClose.closed', { key, sha: String(commit.oid).slice(0, 7) }), i18n.t('taskLinks.autoClose.closedTitle'));
    }
  }, [enabled, readOnly, commits, board, teamId, teams, keysOf, updateBoard]);
}
